import React, { useEffect, useState } from 'react';
import styles from '../../Components/EditDiv/EditDiv.module.css';
import buttonStyles from '../../Components/YesNoButtonsCSS/YesNoButtons.module.css';
import BlueLogo from '../../assets/BlueLogo.png';
import DatePicker from '../DatePicker/DatePicker';
import Dropdown from '../../Components/DropDownSelect/Dropdown';
import moment from 'moment';
import { useDispatch } from '../../redux/store';
import {
  fetchEmployeeAccess,
  offboardEmployee,
  searchEmployeeByEmail,
  updateEmployeeAccess,
} from '../../redux/slices/manageMomentUser';

const moduleOptions = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'opportunities', label: 'Opportunities' },
  { key: 'projects', label: 'Projects' },
  { key: 'timesheets', label: 'Time Sheets' },
  { key: 'clients', label: 'Clients' },
  { key: 'invoicing', label: 'Invoicing' },
  { key: 'suppliers', label: 'Suppliers' },
  { key: 'sharepoint', label: 'SharePoint' },
  { key: 'planner', label: 'Planner Board' },
  { key: 'resourcing', label: 'Resourcing' },
];

const permissionLevelOptions = [
  { id: 1, name: 'User' },
  { id: 2, name: 'Admin' },
  { id: 3, name: 'Holiday Organiser + Admin' },
  { id: 4, name: 'Guest' }
];

function ManageMomentUserPopup({ onClose }) {
  const dispatch = useDispatch();
  const [query, setQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState('');
  const [employee, setEmployee] = useState(null);
  const [searchPerformed, setSearchPerformed] = useState(false);

  // Access state
  const [access, setAccess] = useState(() => {
    const initial = {}; moduleOptions.forEach(({ key }) => { initial[key] = true; }); return initial;
  });
  const [permissionLevel, setPermissionLevel] = useState(1);
  const [savingAccess, setSavingAccess] = useState(false);

  // Offboard state
  const [leaverDate, setLeaverDate] = useState(moment().format('DD/MM/YYYY'));
  const [offboarding, setOffboarding] = useState(false);
  const searchPromiseRef = React.useRef(null);
  const accessPromiseRef = React.useRef(null);

  const handleSearch = async () => {
    if (!query.trim()) { setError('Enter an employee email to search.'); return; }
    setError(''); setIsSearching(true); setSearchPerformed(true);
    try {
      searchPromiseRef.current?.abort?.();
      const promise = dispatch(searchEmployeeByEmail({ email: query }));
      searchPromiseRef.current = promise;
      const employee = await promise.unwrap();
      setEmployee(employee);
    } catch (e) { setEmployee(null); setError(e.message); } finally { setIsSearching(false); }
  };

  // Load access on employee change
  useEffect(() => {
    const fetchAccess = async () => {
      if (!employee?.id) return;
      try {
        accessPromiseRef.current?.abort?.();
        const promise = dispatch(fetchEmployeeAccess({ employeeId: employee.id }));
        accessPromiseRef.current = promise;
        const json = await promise.unwrap();
        // default all true
        const current = {}; moduleOptions.forEach(({ key }) => { current[key] = true; });
        const allowed = json?.allowed_pages || json?.data?.allowed_pages || json?.data || [];
        if (Array.isArray(allowed) && allowed.length > 0) {
          moduleOptions.forEach(({ key }) => { current[key] = false; });
          allowed.forEach((k) => { if (k in current) current[k] = true; });
        }
        setAccess(current);
        const lvl = json?.permission_level ?? json?.data?.permission_level ?? json?.moment_admin_permission_level ?? json?.data?.moment_admin_permission_level;
        if (lvl) setPermissionLevel(Number(lvl));
      } catch (e) { /* keep defaults */ }
    };
    fetchAccess();
    return () => accessPromiseRef.current?.abort?.();
  }, [dispatch, employee]);

  const toggleAccess = (key) => setAccess((prev) => ({ ...prev, [key]: !prev[key] }));
  const handleDateChange = (newDate) => setLeaverDate(moment(newDate.startDate).format('DD/MM/YYYY'));

  const saveAccess = async () => {
    if (!employee?.id) return; setSavingAccess(true);
    try {
      const allowed_pages = moduleOptions.filter(({ key }) => access[key]).map(({ key }) => key);
      await dispatch(updateEmployeeAccess({
        employeeId: employee.id,
        allowedPages: allowed_pages,
        permissionLevel,
      })).unwrap();
    } catch (e) { setError(e.message || 'Failed to save'); } finally { setSavingAccess(false); }
  };

  const confirmOffboard = async () => {
    if (!employee?.id) return; setOffboarding(true);
    try {
      const formattedLeaverDate = moment(leaverDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
      await dispatch(offboardEmployee({
        employeeId: employee.id,
        email: employee.work_email || employee.email,
        leaverDate: formattedLeaverDate,
      })).unwrap();
      onClose();
    } catch (e) { setError(e.message || 'Offboard failed'); } finally { setOffboarding(false); }
  };

  // --- Derived status helpers ---
  const employeeStatusText = (() => {
    const raw = (employee?.status || employee?.current_status || employee?.moment_status || employee?.employment_status || employee?.Status || '').toString();
    return raw;
  })();
  const isLeaverOrEx = Boolean(
    employee && (
      employee?.is_ex_employee === true ||
      employee?.is_leaver === true ||
      /ex\s*-?\s*employee/i.test(employeeStatusText) ||
      /leaver|past user/i.test(employeeStatusText)
    )
  );

  return (
    <div className={styles.backGround} style={{ position: 'fixed', alignItems: 'flex-start' }} onClick={onClose}>
      <div className={styles.form} style={{ width: '560px' }} onClick={(e) => e.stopPropagation()}>
        <div className={styles.momentTitle}>MOMENT</div>
        <div className={styles.sectionTitle}>Manage Moment User</div>

        {error && (
          <div style={{ color: 'red', marginBottom: '12px', textAlign: 'center' }}>{error}</div>
        )}

        {/* Search */}
        <div style={{ marginTop: '10px', marginBottom: '6px' }}>
          <p style={{ fontWeight: 'bold', fontSize: '16px', color: '#3C96D4', borderBottom: '1px solid #e0e0e0', paddingBottom: '5px' }}>Search for Employee</p>
        </div>
        <div className={styles.List}>
          <p className={styles.dataTitleV2}>Employee Email*: </p>
          <div className={styles.commentHolder} style={{ display: 'flex', gap: '10px' }}>
            <input
              className={styles.Comment}
              style={{ height: '20px', flex: 1 }}
              type='email'
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder='employee@blueengineering.co.uk or name@re-structured.com'
            />
            <button type='button' onClick={handleSearch} className={styles.searchButton} disabled={isSearching}>
              {isSearching ? 'Searching...' : 'Search'}
            </button>
          </div>
        </div>

        {isSearching && (
          <div style={{ display: 'flex', justifyContent: 'center', color: '#3C96D4', fontSize: '13px', marginTop: '6px' }}>
            Loading user...
          </div>
        )}

        {/* Details & controls */}
        {searchPerformed && employee && (
          <>
            <div style={{ marginTop: '15px', marginBottom: '10px' }}>
              <p style={{ fontWeight: 'bold', fontSize: '16px', color: '#3C96D4', borderBottom: '1px solid #e0e0e0', paddingBottom: '5px' }}>Employee Details</p>
            </div>
            <div className={styles.List}>
              <p className={styles.dataTitleV2}>Name: </p>
              <div className={styles.commentHolder}><p style={{ margin: 0, fontWeight: 'bold' }}>{employee.first_name} {employee.last_name}</p></div>
            </div>
            <div className={styles.List}>
              <p className={styles.dataTitleV2}>Position: </p>
              <div className={styles.commentHolder}><p style={{ margin: 0 }}>{employee.job_title || 'Not specified'}</p></div>
            </div>

            {/* Access section */}
            <div style={{ marginTop: '14px', marginBottom: '6px' }}>
              <p style={{ fontWeight: 'bold', fontSize: '16px', color: '#3C96D4', borderBottom: '1px solid #e0e0e0', paddingBottom: '5px' }}>Access Levels</p>
            </div>
            <div className={styles.List}>
              <p className={styles.dataTitleV2}>Permission Level: </p>
              <div className={styles.commentHolder}>
                <Dropdown
                  options={permissionLevelOptions.map((o) => o.name)}
                  selectedValue={(permissionLevelOptions.find((o) => o.id === permissionLevel) || permissionLevelOptions[0]).name}
                  onValueChange={(label) => { const opt = permissionLevelOptions.find((o) => o.name === label) || permissionLevelOptions[0]; setPermissionLevel(opt.id); }}
                />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 12px', padding: '6px 4px' }}>
              {moduleOptions.map(({ key, label }) => (
                <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontFamily: 'PT Sans', fontSize: '13px' }}>
                  <input type='checkbox' checked={!!access[key]} onChange={() => toggleAccess(key)} />
                  {label}
                </label>
              ))}
            </div>
            <div className={styles.updateAndCancel} style={{ marginTop: '12px' }}>
              <div className={styles.updateButton} onClick={saveAccess}>
                <div className={buttonStyles.YesButtonComponentStyling}>{savingAccess ? 'SAVING...' : 'SAVE ACCESS'}</div>
              </div>
            </div>

            {/* Offboard section */}
            <div style={{ marginTop: '18px', marginBottom: '10px' }}>
              <p style={{ fontWeight: 'bold', fontSize: '16px', color: '#3C96D4', borderBottom: '1px solid #e0e0e0', paddingBottom: '5px' }}>Offboard</p>
            </div>
            {isLeaverOrEx && (
              <div style={{ color: 'red', margin: '4px 0 10px 0', textAlign: 'center', fontWeight: 'bold' }}>
                This employee cannot be offboarded. Their current status is: {employeeStatusText || 'Unknown'}
              </div>
            )}
            <div className={styles.List} style={isLeaverOrEx ? { opacity: 0.5, pointerEvents: 'none' } : undefined}>
              <p className={styles.dataTitleV2}>Past User Date*: </p>
              <div className={styles.commentHolder}>
                <DatePicker initialDate={leaverDate} onDatesChange={handleDateChange} visableIcon={true} />
              </div>
            </div>
            <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#f8f8f8', borderRadius: '5px', border: '1px solid #e0e0e0', ...(isLeaverOrEx ? { opacity: 0.5 } : {}) }}>
              <p style={{ color: '#e74c3c', margin: '0 0 8px 0', fontWeight: 'bold' }}>Warning:</p>
              <ul style={{ margin: 0, paddingLeft: '18px' }}>
                <li>Sets status to "Past User" and records past user date</li>
                <li>Removes access to Moment</li>
                <li>Notifies DevOps to update O365/email/licenses</li>
              </ul>
            </div>
            <div className={styles.updateAndCancel} style={{ marginTop: '12px' }}>
              <div className={styles.updateButton} onClick={isLeaverOrEx ? undefined : confirmOffboard}>
                <div className={buttonStyles.YesButtonComponentStyling}>{offboarding ? 'PROCESSING...' : 'CONFIRM OFFBOARD'}</div>
              </div>
              <div className={styles.cancelButton} onClick={onClose}>
                <div className={buttonStyles.NoButtonComponentStyling}>CANCEL</div>
              </div>
            </div>
          </>
        )}

        {!employee && (
          <div className={styles.updateAndCancel} style={{ marginTop: '16px' }}>
            <div className={styles.cancelButton} onClick={onClose}>
              <div className={buttonStyles.NoButtonComponentStyling}>CLOSE</div>
            </div>
          </div>
        )}

        <img className={styles.blueLogo} src={BlueLogo} alt='BlueLogo' />
      </div>
    </div>
  );
}

export default ManageMomentUserPopup;


